import { v4 as uuid } from 'uuid';

/**
 * Owns one Authenticate Source details→token→cloud-link attempt.
 * Presentation timeout never settles the raw flight; only actual completion
 * or a classified terminal result does. A superseded generation cannot apply UI.
 * Identity (source, Bridge, room, flow) is captured at begin and compared at
 * every continuation so a stale closure cannot write after a retarget.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global).
 */

export type DirectLinkAttemptIdentity = {
  sourceId?: string | undefined;
  agentId?: string | undefined;
  roomId?: string | undefined;
  flow?: string | undefined;
};

export type DirectLinkAttempt = {
  readonly id: string;
  readonly generation: number;
  readonly identity: DirectLinkAttemptIdentity;
  presentationTimedOut: boolean;
  settled: boolean;
};

export type DirectLinkAttemptOwner = {
  /** Null when a previous attempt's raw work has not settled. */
  begin: (identity?: DirectLinkAttemptIdentity) => DirectLinkAttempt | null;
  /** True while this attempt is the live dialog generation and not yet settled. */
  isCurrent: (attempt: DirectLinkAttempt) => boolean;
  markPresentationTimeout: (attempt: DirectLinkAttempt) => boolean;
  /** Every terminal path must call this once — success, error, late, superseded. */
  settle: (attempt: DirectLinkAttempt) => void;
  supersede: () => void;
  rawPending: () => boolean;
  current: () => DirectLinkAttempt | null;
  resetForTests: () => void;
};

const identityKey = (identity: DirectLinkAttemptIdentity): string =>
  `${identity.sourceId ?? ''}:${identity.agentId ?? ''}:${identity.roomId ?? ''}:${identity.flow ?? ''}`;

/** Live source/Bridge/room/flow must still match the captured attempt. */
export const directLinkAttemptMatches = (
  attempt: DirectLinkAttempt,
  live: DirectLinkAttemptIdentity
): boolean => identityKey(attempt.identity) === identityKey(live);

export const createDirectLinkAttemptOwner = (): DirectLinkAttemptOwner => {
  let generation = 0;
  let current: DirectLinkAttempt | null = null;
  let inFlight = 0;

  const owner: DirectLinkAttemptOwner = {
    begin(identity = {}) {
      // A display timeout must not free this slot for a second write.
      if (inFlight > 0) return null;
      inFlight += 1;
      current = {
        id: `auth-${uuid()}`,
        generation,
        identity: { ...identity },
        presentationTimedOut: false,
        settled: false
      };
      return current;
    },

    isCurrent(attempt) {
      return current === attempt && attempt.generation === generation && !attempt.settled;
    },

    markPresentationTimeout(attempt) {
      if (attempt.settled) return false;
      attempt.presentationTimedOut = true;
      return owner.isCurrent(attempt);
    },

    settle(attempt) {
      if (attempt.settled) return;
      attempt.settled = true;
      inFlight = Math.max(0, inFlight - 1);
    },

    supersede() {
      generation += 1;
    },

    rawPending() {
      return inFlight > 0;
    },

    current() {
      return current;
    },

    resetForTests() {
      generation = 0;
      current = null;
      inFlight = 0;
    }
  };

  return owner;
};
